import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { App } from "antd";

import { useGetCertificationQuery } from "@/redux/service";
import DetailsSkeleton from "@/features/certifications/details/components/DetailsSkeleton";
import { certificationPaths } from "@/domains/certifications/routes";
import type { CertificationLocationState } from "@/domains/certifications/types";
import {
  isNotEnrolledError,
  isRemovedFromCertificationError,
} from "@/domains/certifications/utils";
import CertificationDetails from "@/features/certifications/details";
import NotEnrolled from "@/features/certifications/not-enrolled";
import { ENROLLMENT_STATES } from "@/domains/certifications/constants";

const CertificationDetailsPage = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { slug } = useParams();
  const location = useLocation();
  const {
    data: certification,
    error,
    isError,
  } = useGetCertificationQuery(slug ?? skipToken);
  const isRemoved = isRemovedFromCertificationError(error);

  useEffect(() => {
    if (isRemoved) {
      notification.error({
        key: "certification-removed",
        message: t("certification.removed.title"),
        description: t("certification.removed.body"),
      });
    }
  }, [isRemoved, notification, t]);

  if (isNotEnrolledError(error)) {
    return <NotEnrolled />;
  }

  if (!slug || isError) {
    return <Navigate to="/" replace />;
  }

  if (!certification) {
    return <DetailsSkeleton />;
  }

  const state = location.state as CertificationLocationState | null;
  const isEnrolled =
    certification.enrollment?.state === ENROLLMENT_STATES.ACTIVE;

  if (isEnrolled && !state?.fromOverview) {
    return <Navigate to={certificationPaths.overview(slug)} replace />;
  }

  return <CertificationDetails certification={certification} />;
};

export default CertificationDetailsPage;
